import { Component, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DashboardService } from '../../../../../service/events/dashboard.service';
import { TareasApiService } from '../../../../../service/api/tareas-api.service';
import { FincasApiService } from '../../../../../service/api/fincas-api.service';
import { ClientesApiService } from '../../../../../service/api/clientes-api.service';
import { TrabajadoresApiService } from '../../../../../service/api/trabajadores-api.service';
import { BaseForm } from '../../../../base-form';
import { TareasBioFormDefinition } from './form-control/form-definition';
import { build, buildParams, pool } from '../../../../../common/classes/request-builder';
import { environment } from '../../../../../../environments/environment';
import moment from 'moment';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

import type { OnDestroy, OnInit } from '@angular/core';
import { TareasBioModel } from '../../../../../models/tareas/tareas-bio.model';
import { ParcelasModel } from '../../../../../models/form-common/parcelas.model';
import { MantenimientoModel } from '../../../../../models/trabajadores/mantenimiento.model';
import { FincasModel } from '../../../../../models/form-common/fincas.module';
import type {
    ReactiveFormChangedField,
    ReactiveFormValue,
} from '../../../../../common/components/reactive-form-builder/interfaces/reactive-form-change.interface';
import { FormType } from 'src/app/common/components/reactive-form-builder/interfaces/form.interface';

@Component({
    selector: 'app-tareas-bio-pulverizacion',
    standalone: false,
    templateUrl: './tareas_bio-form.component.html',
    styleUrls: ['./tareas_bio-form.component.scss']
})
export class TareasBioFormComponent extends BaseForm<TareasBioModel> implements OnInit, OnDestroy {
    @ViewChild('imagenCapturada') imagenCapturada: ElementRef = new ElementRef('');

    public readonly appName = environment.appName;
    public readonly logoUrl = environment.logoUrl;
    public readonly hasClients = environment.features.hasClients;
    public readonly showGenerateRecetaBio = environment.features.showGenerateRecetaBio;

    public form!: TareasBioFormDefinition;
    public override model: TareasBioModel = {};
    public formReady = false;
    public loadingData = true;

    public showRecetaTareaBio = false;
    public isGeneratingCanvas = false;
    public receta: TareasBioModel = {};
    public parcela: ParcelasModel = {
        id: '',
        nombre: '',
        superficie: '',
        finca: '',
        sector: ''
    };

    constructor(
        public override route: ActivatedRoute,
        public override dashboard: DashboardService,
        public fincasApi: FincasApiService,
        public clientesApi: ClientesApiService,
        public tareasApi: TareasApiService,
        public trabajadores: TrabajadoresApiService,
        public override router: Router,
    ) {
        super(
            route,
            router,
            dashboard,
            tareasApi.bio.PUT,
            tareasApi.bio.POST,
            'bio',
            'Actualizar Tarea Bio',
            'Realizar Tarea Bio',
            'Duplicar Tarea Bio'
        );
    }

    public async ngOnInit(): Promise<void> {
        if (this.route.snapshot.paramMap.get('deCalendario')?.toString() === 'true') {
            this.pathToGoBack = 'tratamientos-control';
        }

        this.softInit(this.getType());
        this.form = new TareasBioFormDefinition(this.getType(), this.getModel());
        this.form.model = this.model;

        await this.initFormData();

        this.formReady = true;
    }

    public override ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    public formChanges(
        formModel: ReactiveFormValue<TareasBioModel>,
        tag: ReactiveFormChangedField<TareasBioModel>,
    ): void {
        const changedFieldName = String(tag);

        this.form.model = {
            ...this.form.model,
            ...formModel,
        } as TareasBioModel;
        this.model = this.form.model as TareasBioModel;

        if (changedFieldName === 'hora_inicio' || changedFieldName === 'hora_fin') {
            this.updateHorasTotales();
        }

        if (changedFieldName === 'id_cliente') {
            this.reloadClientDropdowns();
        }
    }

    public generarReceta(): void {
        this.receta = {
            ...this.model,
            finca_nombre: this.form.fincas.selected?.nombre ?? this.model.finca_nombre ?? '',
            dosis1: this.concatDoseAndUnit(this.model.dosis1, this.model.ud_real_1),
            dosis2: this.concatDoseAndUnit(this.model.dosis2, this.model.ud_real_2),
            dosis3: this.concatDoseAndUnit(this.model.dosis3, this.model.ud_real_3),
            fecha: this.model.fecha ? moment(this.model.fecha).format('LL') : '',
        };

        this.showRecetaTareaBio = true;
        const elements = document.getElementsByClassName('p-dialog-content');

        setTimeout(() => {
            if (elements[0]) {
                elements[0].scrollTop = 0;
            }
        }, 100);
    }

    public imprimirReceta(): void {
        this.isGeneratingCanvas = true;
        const config = {
            canvasScale: 2,
            width: 220,
        };
        const canvasElement = document.getElementById('html2canvasDiv');

        html2canvas(canvasElement ?? new HTMLElement(), { scale: config.canvasScale, logging: true }).then(canvas => {
            this.isGeneratingCanvas = false;

            const pdf = new jsPDF('l', 'mm', 'a4');
            const data = canvas.toDataURL('image/png');

            pdf.addImage(data, 'png', 10, 10, config.width, canvas.height / (canvas.width / config.width), 'alias', 'FAST');
            pdf.save('Bio.pdf');

            return;
        }).catch((error: unknown) => {
            this.isGeneratingCanvas = false;
            console.log('catch en imprimirReceta: ' + error);
        });
    }

    private async initFormData(): Promise<boolean> {
        this.loadingData = true;
        this.dropdownsFinalized = false;

        return pool([
            build(this.fincasApi.fincasGET, this.form.fincas, 'nombre', true),
            build(this.fincasApi.sectoresGET, this.form.sectores, 'nombre', true),
            build(this.fincasApi.parcelasGET, this.form.parcelas, 'nombre', true),
            build(this.clientesApi.clientesGET, this.form.clientes, { or: ['razon_social', 'nombre'] }),
            build(this.trabajadores.trabajadoresGET, this.form.trabajadores,
                (it: MantenimientoModel) => `${it.nombre} ${it.apellidos || ''}`,
                false,
                true),
            build(this.trabajadores.maquinariaGET, this.form.maquinarias, 'nombre', true),
        ], () => {
            this.dropdownLoadTrigger.next();
        }).then(() => {
            this.applySingleFincaDefault();
            this.loadingData = false;
            this.dropdownsFinalized = true;
            this.dropdownLoadTrigger.next();

            return true;
        }).catch((error: unknown) => {
            this.loadingData = false;
            this.dropdownsFinalized = true;
            this.dropdownLoadTrigger.next();
            console.error('Error loading tareas bio form data.', error);

            return true;
        });
    }

    private reloadClientDropdowns(): void {
        const selectedClientId = this.getReactiveValueId(this.form.model.id_cliente);

        if (!selectedClientId || this.appName === 'agricar') {
            return;
        }

        this.dropdownsFinalized = false;
        this.dropdownLoadTrigger.next();

        pool([
            buildParams(this.fincasApi.fincasGET, { id_cliente: selectedClientId }, this.form.fincas, 'nombre', true),
            buildParams(this.fincasApi.sectoresGET, { id_cliente: selectedClientId }, this.form.sectores, 'nombre', true),
            buildParams(this.fincasApi.parcelasGET, { id_cliente: selectedClientId }, this.form.parcelas, 'nombre', true),
        ], () => {
            this.form.fincas.filtered = this.form.fincas.values;
            this.form.sectores.filtered = [];
            this.form.parcelas.values = this.form.parcelas.values.filter((it) => it.label !== '...');
            this.dropdownLoadTrigger.next();
        }).then(() => {
            this.dropdownsFinalized = true;
            this.dropdownLoadTrigger.next();

            return;
        }).catch((error: unknown) => {
            this.dropdownsFinalized = true;
            this.dropdownLoadTrigger.next();
            console.log('catch en reloadClientDropdowns: ' + error);
        });
    }

    private updateHorasTotales(): void {
        const horasTotales = this.form.calculateHorasTotales();

        if (this.form.model.horas_totales === horasTotales) {
            return;
        }

        this.form.model = {
            ...this.form.model,
            horas_totales: horasTotales,
        };
        this.model = this.form.model as TareasBioModel;

        this.formBuilderRef?.updateForm({ horas_totales: horasTotales }, true);
    }

    private applySingleFincaDefault(): void {
        if (this.getType() !== FormType.CREATION || this.form.fincas.filtered.length !== 2) {
            return;
        }

        const finca = this.form.fincas.filtered[1]?.value as FincasModel | null;

        if (!finca?.id) {
            return;
        }

        this.model = {
            ...this.model,
            id_finca: finca.id,
        };
        this.form.model = this.model;
    }

    private concatDoseAndUnit(dose: unknown, unit: unknown): string {
        return [dose, unit]
            .map((value) => String(value ?? '').trim())
            .filter((value) => value.length > 0)
            .join(' ');
    }

    private getReactiveValueId(value: unknown): string {
        if (typeof value === 'object' && value !== null && 'id' in value) {
            return String((value as { id?: unknown }).id ?? '');
        }

        return String(value ?? '');
    }
}
